export type Network = "BITCOIN" | "ETH_MAINNET" | "BSC" | "SOLANA" | "POLYGON" | "TRON";

// Single source of truth for the supported crypto types. Defined as a
// literal `as const` tuple so z.enum() can consume it directly (zod's
// signature requires a tuple with at least one element, not a generic
// `readonly CryptoType[]`). Order matters — it controls display order
// on the sell page. Adding a fifth coin tomorrow is a one-line edit.
export const ALL_CRYPTO_TYPES = ["BTC", "ETH", "SOL", "USDT"] as const;
export type CryptoType = (typeof ALL_CRYPTO_TYPES)[number];

// Networks where the platform holds its own hot wallets (sweep flows,
// wallet generation, balance checks). Distinct from `Network` — these
// are the chains we have private keys for, while `Network` is every
// chain a user can deposit on (TRON + BITCOIN are accepted for
// deposits but routed via ff.io, no platform-owned wallet there).
//
// Same `as const` pattern as ALL_CRYPTO_TYPES so z.enum() consumes it
// directly. Keep alphabetic for predictable listing in admin UIs.
export const PLATFORM_NETWORKS = ["BSC", "ETH_MAINNET", "POLYGON", "SOLANA"] as const;
export type PlatformNetwork = (typeof PLATFORM_NETWORKS)[number];

export function isPlatformNetwork(value: string): value is PlatformNetwork {
  return (PLATFORM_NETWORKS as readonly string[]).includes(value);
}

export type NetworkInfo = {
  network: Network;
  label: string;
  estimatedFeeUsd: string;   // human-readable range
  confirmationsRequired: number;
  riskLevel: "low" | "medium" | "high";
  warning: string | null;
};

export const NETWORK_INFO: Record<Network, NetworkInfo> = {
  BITCOIN: {
    network: "BITCOIN",
    label: "Bitcoin",
    estimatedFeeUsd: "$1 – $5",
    confirmationsRequired: 2,
    riskLevel: "low",
    warning: null,
  },
  ETH_MAINNET: {
    network: "ETH_MAINNET",
    label: "Ethereum (Mainnet)",
    estimatedFeeUsd: "$5 – $30",
    confirmationsRequired: 12,
    riskLevel: "high",
    warning:
      "Ethereum mainnet fees (gas) are high and vary with network congestion. " +
      "Sending USDT on Ethereum can cost $10–$30 in gas fees paid by you. " +
      "Consider using BSC or Solana for lower fees.",
  },
  BSC: {
    network: "BSC",
    label: "BNB Smart Chain (BSC)",
    estimatedFeeUsd: "$0.10 – $0.50",
    confirmationsRequired: 15,
    riskLevel: "medium",
    warning:
      "BSC is cheaper than Ethereum mainnet but is a separate network. " +
      "Ensure you send funds on BSC — sending ETH mainnet funds to this address will result in loss.",
  },
  SOLANA: {
    network: "SOLANA",
    label: "Solana",
    estimatedFeeUsd: "< $0.01",
    confirmationsRequired: 1,
    riskLevel: "low",
    warning: null,
  },
  POLYGON: {
    network: "POLYGON",
    label: "Polygon (MATIC)",
    estimatedFeeUsd: "< $0.10",
    confirmationsRequired: 20,
    riskLevel: "low",
    warning:
      "Polygon uses the same address format as Ethereum. " +
      "Ensure you send on the Polygon network — sending on Ethereum mainnet to this address will result in loss.",
  },
  TRON: {
    network: "TRON",
    label: "Tron (TRC-20)",
    estimatedFeeUsd: "< $1.00",
    confirmationsRequired: 20,
    riskLevel: "low",
    warning:
      "Tron addresses start with 'T'. Ensure you send USDT on the Tron network only — " +
      "sending from a different chain to this address will result in loss.",
  },
};

// Which networks are valid for each crypto type
export const CRYPTO_NETWORKS: Record<CryptoType, Network[]> = {
  BTC: ["BITCOIN"],
  ETH: ["ETH_MAINNET", "BSC"],
  USDT: ["ETH_MAINNET", "BSC", "SOLANA", "POLYGON", "TRON"],
  SOL: ["SOLANA"],
};

export function getNetworkInfo(network: Network): NetworkInfo {
  return NETWORK_INFO[network];
}

export function getSupportedNetworks(crypto: CryptoType): NetworkInfo[] {
  return CRYPTO_NETWORKS[crypto].map((n) => NETWORK_INFO[n]);
}
